import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip as LTooltip } from "react-leaflet";
import L from "leaflet";
import { fetchFarm, fetchThresholds } from "@/lib/api";
import {
    fdiLevel, deltaTLevel, frostLevel, RISK_COLORS, fmt,
} from "@/lib/risk";
import { cn } from "@/lib/utils";

// react-leaflet needs the default icon path fix – we use CircleMarker so it's not strictly required,
// but this keeps popups etc. safe.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LAYERS = [
    { key: "fdi", label: "HFDI" },
    { key: "delta_t", label: "Delta T" },
    { key: "frost", label: "Frost" },
    { key: "rain_24h", label: "Rain 24h" },
    { key: "rain_7d", label: "Rain 7d" },
    { key: "temp", label: "Temperature" },
];

function levelFor(layer, s, thr) {
    switch (layer) {
        case "fdi": return fdiLevel(s.current.fdi, thr.fdi);
        case "delta_t": return deltaTLevel(s.current.delta_t, thr.delta_t);
        case "frost": return frostLevel(s.forecast_min, thr.frost);
        case "rain_24h": return s.rain_24h >= 5 ? "green" : s.rain_24h > 0 ? "yellow" : "orange";
        case "rain_7d": return s.rain_7d >= 10 ? "green" : s.rain_7d >= 3 ? "yellow" : "orange";
        case "temp": return s.current.temp > 32 ? "red" : s.current.temp > 25 ? "orange" : s.current.temp > 15 ? "yellow" : "green";
        default: return "green";
    }
}

function valueFor(layer, s) {
    switch (layer) {
        case "fdi": return `FDI ${fmt(s.current.fdi, 0)}`;
        case "delta_t": return `${fmt(s.current.delta_t, 1)}°C`;
        case "frost": return `Min ${fmt(s.forecast_min, 1)}°C`;
        case "rain_24h": return `${fmt(s.rain_24h, 1)} mm`;
        case "rain_7d": return `${fmt(s.rain_7d, 1)} mm`;
        case "temp": return `${fmt(s.current.temp, 1)}°C`;
        default: return "";
    }
}

export default function MapPage() {
    const { data } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm, refetchInterval: 60_000 });
    const { data: thr } = useQuery({ queryKey: ["thresholds"], queryFn: fetchThresholds });
    const [layer, setLayer] = useState("fdi");

    if (!data || !thr) return <div className="h-96 bg-white rounded-2xl border animate-pulse" />;

    return (
        <div className="space-y-5" data-testid="map-page">
            <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Farm Map</h1>
                <p className="text-sm text-slate-500 mt-1">Live station colouring by selected metric</p>
            </div>

            <div className="flex flex-wrap gap-2">
                {LAYERS.map((l) => (
                    <button
                        key={l.key}
                        onClick={() => setLayer(l.key)}
                        data-testid={`map-layer-${l.key}`}
                        className={cn(
                            "px-3.5 py-2 text-sm font-semibold rounded-lg border transition-colors",
                            layer === l.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        )}
                    >
                        {l.label}
                    </button>
                ))}
            </div>

            <div className="fw-card overflow-hidden" style={{ height: 520 }}>
                <MapContainer center={[data.center.lat, data.center.lon]} zoom={12} style={{ height: "100%", width: "100%" }}>
                    <TileLayer
                        attribution='&copy; OpenStreetMap contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {data.stations.map((s) => {
                        const lvl = levelFor(layer, s, thr);
                        const color = RISK_COLORS[lvl].hex;
                        return (
                            <CircleMarker
                                key={s.id}
                                center={[s.lat, s.lon]}
                                radius={16}
                                pathOptions={{ color: "white", weight: 3, fillColor: color, fillOpacity: 0.9 }}
                            >
                                <LTooltip permanent direction="top" offset={[0, -18]} className="fw-map-tooltip">
                                    <strong>{s.name}</strong> · {valueFor(layer, s)}
                                </LTooltip>
                                <Popup>
                                    <div className="min-w-[200px]">
                                        <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{s.name}</div>
                                        <div className="text-xs text-slate-500 mb-2">{s.condition}</div>
                                        <div className="grid grid-cols-2 gap-1 text-xs">
                                            <div>Temp: <strong>{fmt(s.current.temp, 1)}°C</strong></div>
                                            <div>RH: <strong>{fmt(s.current.humidity, 0)}%</strong></div>
                                            <div>Wind: <strong>{fmt(s.current.wind, 1)} km/h</strong></div>
                                            <div>Dir: <strong>{s.current.wind_dir}</strong></div>
                                            <div>Rain 24h: <strong>{fmt(s.rain_24h, 1)} mm</strong></div>
                                            <div>ΔT: <strong>{fmt(s.current.delta_t, 1)}°C</strong></div>
                                        </div>
                                        <Link to={`/stations/${s.id}`} className="mt-2 inline-block text-sm font-semibold text-[var(--fw-accent)] hover:underline">
                                            View station →
                                        </Link>
                                    </div>
                                </Popup>
                            </CircleMarker>
                        );
                    })}
                </MapContainer>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 items-center text-xs text-slate-600">
                <span className="font-semibold uppercase tracking-wider">Legend:</span>
                {["green", "yellow", "orange", "red"].map((l) => (
                    <span key={l} className="inline-flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full" style={{ background: RISK_COLORS[l].hex }} />
                        {l === "green" ? "Low" : l === "yellow" ? "Moderate" : l === "orange" ? "High" : "Extreme"}
                    </span>
                ))}
            </div>
        </div>
    );
}
